import { useMsal } from "@azure/msal-react";
import {
    CommandBarButton,
    DefaultButton,
    IButtonStyles,
    IColumn,
    IconButton,
    IContextualMenuItem,
    IDetailsListStyleProps,
    IDetailsListStyles,
    IIconProps,
    IOverflowSetItemProps,
    IStyleFunctionOrObject,
    ITag,
    mergeStyleSets,
    Modal,
    OverflowSet,
    PrimaryButton,
    SearchBox, Selection,
    SelectionMode,
    Spinner,
    SpinnerSize, Stack, TagPicker,
    Text,
    useTheme
} from "@fluentui/react";
import { RootState } from "app/store";
import { AppLinkToExternal, emitCustomEvent } from "global/Application";
import { MsalTokenSource } from "global/DataSources/MsalTokenSource";
import Formatters from "global/Utility/Formatters";
import { xmlSanitize } from "global/Utility/TSFunctions";
import * as IXrmTypes from "model/CRM";
import * as FAM from 'model/FlexibleAttributeModel';
import { IExtendedColumn } from "model/IExtendedColumn";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { AppDataSource } from "../../global/DataSources/AppDataSource";
import CustomDetailList from "./CustomDetailList";
import { DefaultGridConfig } from "./CustomDetailList/CustomDetailList";
import { ICustomDetailListConfiguration } from "./CustomDetailList/ICustomDetailListConfiguration";
import { IGridItem } from "./CustomDetailList/IGridItem";
import { filterPredicateColumnPropertyString, sortPredicateColumnPropertyString } from "./Grid";

const cancelIcon: IIconProps = { iconName: 'Cancel' };
// const miniExpandIcon: IIconProps = { iconName: 'MiniExpand' };
// const miniContractIcon: IIconProps = { iconName: 'MiniContract' };
interface IProps<T> {
    isOpen: boolean;
    label?: string;
    onDismiss?: () => void;
    onPick?: (values: T[] | null) => void;
    onCancel?: () => void;
    masterAttribute?: IXrmTypes.bcbs_config_masterattribute;
    attribute?: IXrmTypes.bcbs_config_attribute;
    onSearch?: TSearchPredicateAsync<T>;
    transformer?: (input: Record<string, string>) => Record<string, string>;
    variant: "single" | "multiple" | "multiple-keep";
    queryMenuItems?: IContextualMenuItem[];
    queryMenuSelectedKey?: string;
    hideSearch?: boolean;
}

export type TSearchPredicateAsync<T> = (value: string, statusDelegate?: (onMessage: string) => void) => Promise<ISearchResult<T>>;

interface ISearchResult<T> {
    items: IGridItem<T>[];
    columns: IExtendedColumn[];
    showNoResultPrompt: boolean;
    showLimitedResultPrompt: boolean;
}


const gridStyles: IStyleFunctionOrObject<IDetailsListStyleProps, IDetailsListStyles> | undefined = {
    root: {
        marginBottom: '1.45em',
        selectors: {
            '.ms-DetailsRow': {
                borderTop: '1px solid #EAEAEA'
            },
            // '.ms-SelectionZone': {
            //     overflow: 'visible'
            // },
        },
    },
    headerWrapper: {
        selectors: {
            '.ms-DetailsHeader': {
                paddingTop: '0px',
            },
        }
    },

};

interface ICellSchema {
    [key: string]: any;
    Name: string;
    Width: number;
    Format?: "MM/dd/yyyy, hh:mm:ss ET" | "MM/dd/yyyy";
}

interface IRowSchema {
    [key: string]: any;
    Name: string;
    Id: string;
    Cells: ICellSchema[];
}

interface ILayout {
    [key: string]: any;
    Name: string;
    Object: Number;
    Rows: IRowSchema[];
    Jump: string;
}

interface IFetchSchema {
    viewName: string;
    entityName: string;
    entitySetName: string;
    fetchXML: string;
    layout: ILayout;
}


interface IDataTag<T> extends ITag {
    data?: T
}

// https://bcbsonlinedevgl.crm.dynamics.com/api/data/v9.0/savedqueries?$select=name&$orderby=createdon%20desc
// https://bcbsonlinedevgl.crm.dynamics.com/api/data/v9.0/userqueries

const LookupControlDialog = <T extends FAM.IObjectWithNameId>(props: IProps<T>) => {
    const maxTotal = 200;

    // Development Validation:
    if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development')
        if (!props.masterAttribute)
            console.warn('Master attribute is required for this component to function.');

    const theme = useTheme();

    const state = useSelector((state: RootState) => state);

    const { instance } = useMsal();

    const scopeGridConfig = () => {
        const t = DefaultGridConfig();
        return t;
    }
    const [gridConfig, setGridConfig] = useState<ICustomDetailListConfiguration>(scopeGridConfig());

    const [items, setItems] = useState<IGridItem<T>[] | null>(null);

    const [columns, setColumns] = useState<IExtendedColumn[] | null>(null);

    const [gridItems, gridItemsPaged] = useMemo(() => {

        const gridItems: typeof items = [];

        const gridConfigMappedColumns = {
            filterColumns: gridConfig.filter.map(q => columns?.find(w => w.key === q.column.key)),
            sortColumns: gridConfig.sort.map(q => columns?.find(w => w.key === q.column.key)),
        }

        items?.forEach((item, index) => {

            let include = true;

            if (gridConfig && gridConfig.filter && gridConfig.filter.length > 0) {
                gridConfig.filter.forEach((filter, ix) => {
                    const column = gridConfigMappedColumns.filterColumns[ix];

                    if (column) {
                        const predicate = column.filterPredicate;
                        if (predicate)
                            include = include && predicate(item, filter.value, column);
                    }
                });
            }

            if (include)
                gridItems.push(item);
        });


        if (gridConfig && gridConfig.sort && gridConfig.sort.length > 0)

            gridItems.sort((a, b) => {
                let ret = 0;
                gridConfig.sort?.forEach((sort, ix) => {
                    const column = gridConfigMappedColumns.sortColumns[ix];

                    // TODO why was this needed?
                    if (!column)
                        return;

                    const predicate = column.sortPredicate;
                    const isSortedDescending = sort.isSortedDescending ?? false;

                    if (predicate)
                        ret = isSortedDescending
                            ? predicate(a, b, undefined, column) * -1
                            : predicate(a, b, undefined, column);
                });

                return ret;

            });


        const gridItemsPages = (gridItems ?? []).slice((gridConfig.page - 1) * gridConfig.pageSize, gridConfig.page * gridConfig.pageSize);

        return [gridItems, gridItemsPages];
    }, [
        items,
        gridConfig,
        columns,
    ])


    useEffect(() => {
        setColumns(xColumns => {
            const nColumns = (xColumns ?? []).map(xColumn => {
                const column: IExtendedColumn = {
                    ...xColumn,
                    isResizable: true,
                    isSorted: false,
                    isFiltered: false,
                    isSortedDescending: false
                };

                if (gridConfig) {
                    const sortProperty = gridConfig.sort.find(q => q?.column?.key === column.key);
                    if (sortProperty) {
                        column.isSorted = true;
                        column.isSortedDescending = sortProperty.isSortedDescending;
                    }

                    const filterProperty = gridConfig.filter.find(q => q?.column?.key === column.key);
                    if (filterProperty) {
                        column.isFiltered = true;
                    }
                }

                return column!;

            })

            return nColumns;
        });

    }, [gridConfig]);


    const tokenSource = new MsalTokenSource(instance);
    const dataSource = new AppDataSource(tokenSource);

    const [viewModel, setViewModel] = useState({
        processing: null as string | null,
        criteria: null as string | null,
        note: null as JSX.Element | null,
        showNoResultPrompt: false as boolean | null,
        showLimitedResultPrompt: false as boolean | null,
    });


    const [tagItems, setTagItems] = useState<IDataTag<T>[] | null>(null);

    const selection = useMemo(() => {
        return new Selection({
            onSelectionChanged: () => {
                const getSelection = selection.getSelection() as IGridItem<T>[];

                // setSelectedItems(_selections);
                const tags = getSelection.map(q => {

                    const t: IDataTag<T> = {
                        key: q.key,
                        name: q.name,
                        data: q.data
                    };

                    return t;
                });

                setTagItems(prevState => {
                    // are we keeping search session for cases where they perform additional searches
                    if (props.variant && props.variant === "multiple-keep") {

                        // current state spread, ready for insert
                        const currState = [...(prevState ?? [])];
                        currState.push(...tags);

                        const uniqueTags = new Map(currState.map((t) => [t.key, t])).values();

                        return [...uniqueTags];
                    }
                    return tags;
                });
            },
            selectionMode: SelectionMode.multiple,
        })
    }, [props.isOpen]);

    const contentStyles = useMemo(() => {
        return mergeStyleSets({
            container: {
                width: '80%',
                height: '88%',
                borderTop: `4px solid ${theme.palette.themePrimary}`,
            },
            scroll: {
                // overflowY: 'hidden',
                height: '100%'
            },
            header: [
                {
                    borderTop: `4px solid ${theme.palette.themePrimary}`,
                    color: theme.palette.neutralPrimary,
                    padding: '18px 12px 12px 24px',
                },
            ],
            body: {
                paddingLeft: '24px',
                paddingRight: '24px'
            },
            footer: {
                marginLeft: '24px',
                marginRight: '24px',

            }
        });
    }, [theme]);

    const iconButtonStyles: Partial<IButtonStyles> = {
        root: {
            color: theme.palette.neutralPrimary,
            marginLeft: 'auto',
            marginTop: '4px',
            marginRight: '2px',
        },
        rootHovered: {
            color: theme.palette.neutralDark,
        },
    };

    const onDismiss = () => {
        props.onDismiss?.();
    }

    const onCancel = () => {
        props.onCancel?.();
        onDismiss();
    }
    // const onExpand = () => {
    //     setExpand(!isFullScreen);
    // }

    const onPick = () => {
        const t = tagItems?.map(q => q.data!) ?? []

        props.onPick?.(t.length > 0
            ? t
            : null
        );
    }

    /**
     * Searches for view definitions based on the provided value.
     * @param value The search value.
     * @returns An object containing the search results.
     */
    const searchViewDefinition = async (value: string) => {
        const items = [] as IGridItem<T>[]
        const columns: IExtendedColumn[] = [];

        let noResult: boolean = false;
        let showLimitedResultPrompt: boolean = false;
        let error: string | null = null;

        if (!props?.masterAttribute?.bcbs_lookupviewdefinition) {
            error = `Master/Attribute/Custom Search has not been setup with correct fetchXML configuration!`;
        }

        if (!error) {
            const viewDef = JSON.parse(props?.masterAttribute?.bcbs_lookupviewdefinition!) as IFetchSchema

            let hasWildcard = false;
            let replaceValue = xmlSanitize(value);

            // is this a wild card search, 
            if (replaceValue.includes('*')) {
                replaceValue = replaceValue.replace(/\*/g, '%');
                hasWildcard = true;
            }

            const searchValueRegex = hasWildcard
                ? /%SEARCHVALUE%/g
                : /SEARCHVALUE/g;

            viewDef.fetchXML = viewDef.fetchXML.replace(searchValueRegex, replaceValue);

            if (state.status?.account?.name)
                viewDef.fetchXML = viewDef.fetchXML.replace(/BLUERELATIONSHIP/g, xmlSanitize(state.status.account.name));

            if (state.data.snapshot.bcbs_groupconfigurationsnapshotid)
                viewDef.fetchXML = viewDef.fetchXML.replace(/BCBS_GROUPCONFIGURATIONSNAPSHOTID/g, xmlSanitize(state.data.snapshot.bcbs_groupconfigurationsnapshotid));

            if (state.data.snapshot._bcbs_originatingsnapshotid_value)
                viewDef.fetchXML = viewDef.fetchXML.replace(/BCBS_ORIGINATINGSNAPSHOTID/g, xmlSanitize(state.data.snapshot._bcbs_originatingsnapshotid_value));


            if (state.data.snapshot._bcbs_originatingsnapshot2id_value)
                viewDef.fetchXML = viewDef.fetchXML.replace(/BCBS_ORIGINATINGSNAPSHOT2ID/g, xmlSanitize(state.data.snapshot._bcbs_originatingsnapshot2id_value));


            if (viewDef.fetchXML.indexOf('top=') === -1) {
                viewDef.fetchXML = viewDef.fetchXML.replace('<fetch', `<fetch top='${maxTotal}'`)
            }

            console.debug("[appl]", "LookupControlDialog.searchViewDefinition", viewDef);

            const result = await dataSource.crm.fetch<any>(viewDef.entitySetName, viewDef.fetchXML, { includeAnnotations: true });

            // const columnMap = {} as Record<string, ICustomColumn>;

            const refIdColumn = viewDef.layout.Rows[0].Id;
            const refNameColumn = viewDef.layout.Jump;

            const refPredicate = (row: any) => {
                const ref: T = {
                    type: "entity",
                    entityType: viewDef.entityName,
                    id: row[refIdColumn],
                    name: row[refNameColumn]
                } as any
                return ref;
            }

            result.forEach((eachLine: Record<string, string>, index) => {
                let include = true;

                const t = props.transformer ? props.transformer(eachLine) : eachLine;
                const data = refPredicate(eachLine);

                if (include)
                    items.push({
                        ...t,
                        _index: index,
                        key: index,
                        name: data?.name,
                        data: data,
                    });

                // for (const [key, value] of Object.entries(eachLine)) {
                //     // if already column mapped, ignore
                //     if (columnMap[key])
                //         continue;

                //     columnMap[key] = {
                //         key: key,
                //         minWidth: 100,
                //         maxWidth: 120,
                //         name: key,
                //         data: eachLine,
                //         fieldName: key,
                //         _order: 123,
                //         isResizable: true
                //     }
                // }

            })


            // TODO What have I done here?! 
            // TODO move this to react display layer
            if (items.length === 0)
                noResult = true;

            if (items.length === maxTotal)
                showLimitedResultPrompt = true;

            // Column -------------------------------------------------------
            const formatted = ['Picklist', 'Virtual', 'Status', 'State', 'Lookup', 'Boolean'];
            viewDef.layout.Rows[0].Cells.forEach((eachColumn, index) => {
                const isFormatted = formatted.includes(eachColumn.AttributeType);
                const isDateTime = eachColumn.AttributeType === 'DateTime';

                const isDateTimeDotnet = eachColumn.Format === 'MM/dd/yyyy, hh:mm:ss ET';
                const isDateDotNet = eachColumn.Format === 'MM/dd/yyyy';

                const fieldNameFormatted = eachColumn.Name + '@OData.Community.Display.V1.FormattedValue';

                const fieldName = isFormatted
                    ? fieldNameFormatted
                    : eachColumn.Name;

                const column: IExtendedColumn = {
                    key: eachColumn.Name,
                    minWidth: eachColumn.Width,
                    name: eachColumn.DisplayName ?? eachColumn.Name,
                    fieldName: fieldName,
                    _order: index,
                    isResizable: true,
                };


                // Custom Renderers:
                if (isDateTime) {

                    column.onRender = (item, index, column) => {

                        if (!column?.fieldName || !item)
                            return <div style={{ textAlign: 'left' }}>-</div>

                        let value = item[fieldNameFormatted];
                        if (value)
                            return <div>{value}</div>

                        value = item[column.fieldName];
                        if (value)
                            return <div>{Formatters.ToShortDateTime(value)}</div>

                        return <div style={{ textAlign: 'left' }}>-</div>

                    }

                    if (isDateTimeDotnet) {
                        column.onRender = (item, index, column) => {

                            if (!column?.fieldName || !item)
                                return <div style={{ textAlign: 'left' }}>-</div>

                            let value = item[fieldName];
                            if (value)
                                return <div>{Formatters.ToShortDateTimeAmericaNewYork(value)}</div>

                            return <div style={{ textAlign: 'left' }}>-</div>

                        }
                    } else if (isDateDotNet) {
                        // 2024-01-01T00:00:00Z
                        column.onRender = (item, index, column) => {

                            if (!column?.fieldName || !item)
                                return <div style={{ textAlign: 'left' }}>-</div>

                            let value = item[fieldName];
                            if (value)
                                return <div>{Formatters.ToShortDate(value.replace("Z", ""))}</div>

                            return <div style={{ textAlign: 'left' }}>-</div>

                        }
                    }
                }


                columns.push(column);
            });

        }

        return {
            items: items,
            columns: columns,
            showNoResultPrompt: noResult ?? false,
            showLimitedResultPrompt: showLimitedResultPrompt ?? false,
        };
    };

    const search = async (criteria: string) => {

        let search: TSearchPredicateAsync<T> = searchViewDefinition;

        if (props.onSearch)
            search = props.onSearch;


        try {
            setViewModel(t => {
                return {
                    ...t,
                    processing: 'Searching',
                    note: null,
                    criteria: criteria,
                    showNoResultPrompt: false,
                    showLimitedResultPrompt: false,
                }
            });

            const statusDelegate = (onMessage: string) => {
                if (onMessage)
                    setViewModel(vm => {
                        return {
                            ...vm,
                            processing: onMessage
                        }
                    });
            }

            const {
                items,
                columns,
                showNoResultPrompt: showNoResultPrompt,
                showLimitedResultPrompt: showLimitedResultPrompt,
            } = await search(criteria, statusDelegate);

            setItems(items);

            const onColumnClick = (event: React.MouseEvent<HTMLElement, MouseEvent>, column: IColumn) => {
                setGridConfig(t => {
                    const gridConfig: ICustomDetailListConfiguration = {
                        sort: [],
                        filter: [],
                        pageSize: 10,
                        page: 1,
                    };

                    gridConfig.sort = [{
                        column: { key: column.key },
                        isSortedDescending: !t?.sort?.[0]?.isSortedDescending ?? false,
                    }];

                    if (t && t.filter && t.filter.length && t.filter.length > 0) {
                        gridConfig.filter = t.filter;
                    }

                    if (t?.pageSize)
                        gridConfig.pageSize = t?.pageSize;

                    return gridConfig;
                });
            };

            columns.forEach(column => {
                column.onColumnClick = onColumnClick;
                column.sortPredicate = sortPredicateColumnPropertyString;
                column.filterPredicate = filterPredicateColumnPropertyString;
            });

            setColumns(columns);
            setGridConfig(t => ({ ...t, page: 1, filter: [] }));

            setViewModel(t => {
                return {
                    ...t,
                    showNoResultPrompt: showNoResultPrompt,
                    showLimitedResultPrompt: showLimitedResultPrompt
                }
            });

        } catch (error) {
            console.error(error);
            emitCustomEvent({
                eventType: 'globalException',
                details: error
            });
            throw error;
        } finally {
            setViewModel(t => {
                return {
                    ...t,
                    processing: null
                }
            });
        }

    }
    const pickerRef = useRef(null)
    const refSearchbox = useRef(null);

    useEffect(() => {
        if (props.isOpen) {
            search(viewModel.criteria ?? '');
        }

    }, [props.isOpen]);


    useEffect(() => {
        if (props.queryMenuSelectedKey) {
            search(viewModel.criteria ?? '');
        }

    }, [props.queryMenuSelectedKey]);



    let disableContinue = false;

    if (!tagItems)
        disableContinue = true;
    else if (props.variant === "multiple" && tagItems.length < 1)
        disableContinue = true;
    else if (props.variant === "single" && tagItems.length !== 1)
        disableContinue = true;

    const menu: IOverflowSetItemProps[] = [];
    menu.push({
        key: 'remove', name: 'Remove', icon: 'Edit',
        onClick: () => {

        }
    });

    const canAdd = props?.masterAttribute?.bcbs_lookupentitylogicalname && ['contact', 'bcbs_carrier'].includes(props?.masterAttribute?.bcbs_lookupentitylogicalname);

    const is_bcbs_carrier = props?.masterAttribute?.bcbs_lookupentitylogicalname === 'bcbs_carrier';

    return <Modal
        isOpen={props.isOpen}
        onDismiss={() => { props.onDismiss?.() }}
    >
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', maxHeight: '100%', overflowY: 'auto' }}>

            {/* header */}
            <Stack
                horizontal
                disableShrink={true}
                horizontalAlign={"end"}
                styles={{ root: { padding: '18px 20px 10px 20px' } }}
            >
                <Stack.Item
                    style={{ flex: '1' }}
                >

                    <Text variant="xLargePlus" styles={{ root: { fontWeight: '200' } }}>
                        {`Choose ${props.label ?? 'Lookup'}`}
                    </Text>
                </Stack.Item>

                {/* 
            <IconButton
                styles={iconButtonStyles}
                iconProps={expand
                    ? miniExpandIcon
                    : miniContractIcon
                }
                ariaLabel="Expand"
                onClick={onExpand}
            /> */}

                <IconButton
                    styles={iconButtonStyles}
                    iconProps={cancelIcon}
                    ariaLabel="Close"
                    onClick={onDismiss}
                />
            </Stack>

            {/* body1 */}
            <Stack styles={{ root: { padding: '0px 20px 0px 20px' } }} horizontal verticalAlign="center">

                {props.hideSearch !== true &&
                    <Stack.Item grow={1}>
                        <SearchBox
                            autoFocus
                            componentRef={refSearchbox}
                            onSearch={search}
                            placeholder="Search"
                        />
                    </Stack.Item>
                }

                {canAdd &&
                    <Stack.Item >
                        <OverflowSet
                            onRenderOverflowButton={(props, defaultRender) => {
                                return <CommandBarButton
                                    ariaLabel="More items"
                                    role="menuitem"
                                    menuIconProps={{ iconName: 'More' }}
                                    menuProps={{ items: props! }}
                                />;
                            }}
                            onRenderItem={(item: IOverflowSetItemProps): JSX.Element => {
                                if (item.onRender) {
                                    return item.onRender(item);
                                }
                                return <CommandBarButton
                                    iconProps={{ iconName: item.icon }}
                                    menuProps={item.subMenuProps}
                                    text={item.name}
                                    onClick={item.onClick}
                                />;
                            }}

                            items={[{
                                key: 'newItem',
                                name: 'New',
                                icon: 'Add',
                                onClick: () => {

                                    const extraQS = {
                                        bcbs_groupid: state.data.account?.accountid,
                                        bcbs_groupidname: state.data.account?.name
                                    };

                                    const link = AppLinkToExternal({
                                        id: '',
                                        type: 'customEntityType',
                                        logicalName: props?.masterAttribute?.bcbs_lookupentitylogicalname,
                                        extraQS: extraQS
                                    });

                                    const win = (window as Window).open(link, "New");

                                    if (win)
                                        var timer = setInterval(function () {
                                            if (win.closed) {
                                                clearInterval(timer);
                                                setViewModel(vm => { return { ...vm, processing: null } });
                                                search(viewModel.criteria ?? '');
                                            }
                                        }, 500);

                                    setViewModel(vm => {
                                        return {
                                            ...vm,
                                            processing: `Waiting for (${props?.masterAttribute?.bcbs_lookupentitylogicalname}) screen to close.`
                                        }
                                    });

                                }
                            }]}
                        />
                    </Stack.Item>
                }
            </Stack>

            {/* body2 */}

            <Stack styles={{ root: { paddingLeft: '20px', paddingRight: '20px' } }} >
                {viewModel.processing &&
                    // Opacity %   255 Step        2 digit HEX prefix
                    // 0%          0.00            00
                    // 5%          12.75           0C
                    // 10%         25.50           19
                    // 15%         38.25           26
                    // 20%         51.00           33
                    // 25%         63.75           3F
                    // 30%         76.50           4C
                    // 35%         89.25           59
                    // 40%         102.00          66
                    // 45%         114.75          72
                    // 50%         127.50          7F
                    // 55%         140.25          8C
                    // 60%         153.00          99
                    // 65%         165.75          A5
                    // 70%         178.50          B2
                    // 75%         191.25          BF
                    // 80%         204.00          CC
                    // 85%         216.75          D8
                    // 90%         229.50          E5
                    // 95%         242.25          F2
                    // 100%        255.00          FF
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 999, backgroundColor: `${theme.semanticColors.bodyBackground}C0` }}>
                        <Spinner
                            label={viewModel.processing ?? "Processing"}
                        />
                    </div>
                }
                {items &&
                    <CustomDetailList
                        items={gridItems ?? []}
                        itemsPaged={gridItemsPaged ?? []}
                        columns={columns ?? []}
                        styles={gridStyles}
                        compact={true}
                        selection={selection}
                        getKey={(item, index) => { return item.key; }}
                        onShouldVirtualize={(props) => false}
                        gridConfig={[gridConfig, setGridConfig]}
                        showLimitedResultPrompt={viewModel.showLimitedResultPrompt ?? false}
                        showNoResultPrompt={viewModel.showNoResultPrompt ?? false}
                        // hasPageSettings={false}
                        queryMenuItems={props.queryMenuItems}
                        queryMenuSelectedKey={props.queryMenuSelectedKey}
                    />
                }

            </Stack>

            {/* footer */}
            <Stack
                disableShrink={true}
                horizontal
                verticalAlign="end"
                tokens={{ childrenGap: '10px' }}
                styles={{ root: { padding: '5px 20px 20px 20px' } }}
            >

                <Stack.Item style={{ flex: '1' }}>
                    <Stack horizontalAlign="start">
                        <Text>Selection</Text>
                        <TagPicker
                            styles={{ root: { width: '100%' } }}
                            ref={pickerRef}
                            onResolveSuggestions={(filter, items) => { return [] }}
                            removeButtonAriaLabel="Remove"
                            onBlur={(event) => {
                                // empty as soon as focus leaves
                                if (pickerRef.current)
                                    //@ts-expect-error TS2339
                                    pickerRef?.current?.input?.current?._updateValue?.("");
                            }}
                            selectionAriaLabel="Selected colors"
                            getTextFromItem={(t) => t.name}
                            selectedItems={tagItems ?? []}
                            onChange={(items) => {
                                setTagItems(items ?? null);
                            }}
                            inputProps={{ disabled: true }}
                        />
                    </Stack>

                </Stack.Item>

                <Stack.Item>
                    <PrimaryButton onClick={onPick} text="Select" disabled={disableContinue} />
                </Stack.Item>

                <Stack.Item>
                    <DefaultButton onClick={onCancel} text="Cancel" />
                </Stack.Item>

            </Stack>

        </div>

    </Modal >
}

export default LookupControlDialog